import { forwardRef, useLayoutEffect, useRef, type TextareaHTMLAttributes } from 'react';
import { useTranslation } from 'react-i18next';
import { cx } from '@/shared/lib/cx';
import { mergeRefs } from '@/shared/lib/hooks';
import { useFieldAttributes } from './Input';
import s from './Input.module.css';

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  invalid?: boolean;
  /** Высота по содержимому (без ручного изменения размера). */
  autosize?: boolean;
  /** Показать счётчик символов (нужен maxLength). */
  showCount?: boolean;
  containerClassName?: string;
}

/** Многострочное поле с необязательным счётчиком и авторазмером. */
export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  { invalid: invalidProp, autosize = false, showCount = false, containerClassName, className, id: idProp, required: requiredProp, disabled: disabledProp, maxLength, value, defaultValue, onChange, ...rest },
  ref,
) {
  const { t } = useTranslation('ui');
  const innerRef = useRef<HTMLTextAreaElement | null>(null);
  const { id, invalid, required, disabled, describedBy } = useFieldAttributes({
    id: idProp,
    invalid: invalidProp,
    required: requiredProp,
    disabled: disabledProp,
    'aria-describedby': rest['aria-describedby'],
  });
  const counterId = id ? `${id}-counter` : undefined;
  const length = String(value ?? innerRef.current?.value ?? defaultValue ?? '').length;

  useLayoutEffect(() => {
    const node = innerRef.current;
    if (!autosize || !node) return;
    node.style.height = 'auto';
    node.style.height = `${node.scrollHeight}px`;
  }, [autosize, value]);

  return (
    <div className={s.textareaWrap}>
      <div className={cx(s.control, s.textareaControl, containerClassName)} data-invalid={invalid ? 'true' : undefined} data-disabled={disabled ? 'true' : undefined}>
        <textarea
          ref={mergeRefs(ref, innerRef)}
          id={id}
          className={cx(s.textarea, autosize && s.autosize, className)}
          required={required}
          disabled={disabled}
          maxLength={maxLength}
          value={value}
          defaultValue={defaultValue}
          onChange={(event) => {
            if (autosize) {
              event.currentTarget.style.height = 'auto';
              event.currentTarget.style.height = `${event.currentTarget.scrollHeight}px`;
            }
            onChange?.(event);
          }}
          {...rest}
          aria-invalid={invalid || undefined}
          aria-describedby={[describedBy, showCount && maxLength ? counterId : null].filter(Boolean).join(' ') || undefined}
        />
      </div>
      {showCount && maxLength ? (
        <span id={counterId} className={cx(s.counter, length >= maxLength && s.counterOver)}>
          {t('counter', { current: length, max: maxLength })}
        </span>
      ) : null}
    </div>
  );
});
